import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { planningApi } from "@/domains/Grid/api/planningApi";
import { PostCardAuthProvider } from "@/domains/PostCreator/contexts/PostCardAuthContext";
import { ClientFeedPost } from "./ClientFeedPost";
import { UserRole } from "@/services/urlService";
import type { GridData } from "@/domains/Grid/types/grid.types";
import "./ClientFeedView.css";

interface ClientFeedViewProps {
  clientId: string;
  token?: string;
}

export function ClientFeedView({ clientId, token }: ClientFeedViewProps) {
  const { postIndex } = useParams<{ postIndex: string }>();
  const navigate = useNavigate();
  const [grid, setGrid] = useState<GridData | null>(null);
  const [loading, setLoading] = useState(true);
  const [focusedPostIndex, setFocusedPostIndex] = useState<number | null>(null);
  const postRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);

  const startPostIndex = postIndex ? parseInt(postIndex) : 0;

  useEffect(() => {
    if (!token) {
      return;
    }
    loadActiveGrid();
  }, [clientId, token]);

  useEffect(() => {
    if (grid && postRefs.current[startPostIndex]) {
      setTimeout(() => {
        postRefs.current[startPostIndex]?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [grid, startPostIndex]);

  // Track focused post via the scroll container's position. A scroll listener
  // beats IntersectionObserver here because tall cards (long captions, many
  // notes) can stay below any fixed visibility threshold for their entire
  // pass through the viewport, leaving the observer silent.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !grid) return;

    const HEADER_HEIGHT = 60;
    let rafId: number | null = null;

    const updateFocus = () => {
      rafId = null;
      const containerRect = container.getBoundingClientRect();
      const focusLine = containerRect.top + HEADER_HEIGHT;
      const visibleEnd = containerRect.bottom;

      let bestIdx: number | null = null;
      let bestDistance = Infinity;

      for (const [key, el] of Object.entries(postRefs.current)) {
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.bottom <= focusLine || rect.top >= visibleEnd) continue;
        const distance = Math.abs(rect.top - focusLine);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIdx = parseInt(key);
        }
      }

      if (bestIdx !== null) {
        setFocusedPostIndex(bestIdx);
      }
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(updateFocus);
    };

    updateFocus();
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [grid]);

  const loadActiveGrid = async () => {
    try {
      setLoading(true);
      const grids = await planningApi.listGrids(clientId, true);
      const activeGrid = grids.find((g) => g.isActive);
      setGrid(activeGrid || null);
    } catch (error) {
      console.error("Failed to load active grid:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToGrid = () => {
    navigate(`/client/${clientId}/planning`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center">
        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4" />
        <p>Loading post...</p>
      </div>
    );
  }

  if (!grid) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center">
        <h2>No posts found</h2>
      </div>
    );
  }

  const postsWithContent = grid.gridItems.filter((item) => item.post);

  return (
    <PostCardAuthProvider
      userRole={UserRole.CLIENT}
      clientId={clientId}
      authToken={token}
    >
      <div
        ref={containerRef}
        className="client-feed-view h-screen bg-gray-50 relative overflow-y-scroll snap-y snap-proximity hide-scrollbar"
      >
        {/* Fixed Header with Back Button */}
        <div className="fixed top-0 left-0 right-0 z-20 bg-white border-b border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between w-full gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <button
                onClick={handleBackToGrid}
                className="flex items-center justify-center p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
                title="Back to Grid"
                aria-label="Back to Grid"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              {grid && (
                <h1 className="text-lg font-semibold text-gray-900 truncate">
                  {grid.name}
                </h1>
              )}
            </div>
          </div>
        </div>

        {postsWithContent.map((item) => {
          const post = item.post!;
          const isFocused = focusedPostIndex === item.position;

          return (
            <div
              key={post.id}
              ref={(el) => (postRefs.current[item.position] = el)}
              data-post-index={item.position}
              className="snap-start min-h-screen pt-[60px] w-full flex items-start justify-center pb-4"
            >
              <ClientFeedPost
                post={post}
                gridId={grid.id}
                clientId={clientId}
                isFocused={isFocused}
                onPostUpdated={loadActiveGrid}
              />
            </div>
          );
        })}
      </div>
    </PostCardAuthProvider>
  );
}
